/**
 * Notification Deep Link Resolver
 * 
 * SAFE MODE FIX: Resolves notification data to a navigation target
 * Supports both old and new notification payload formats for backward compatibility.
 * 
 * @version 1.0.0
 * @date 2024-12-16
 * 
 * NOTIFICATION PAYLOAD CONTRACT:
 * - type: 'task' | 'budget' | 'shopping' | 'transaction' | 'goal' | 'system'
 * - route: (optional) explicit route path
 * - entity: { entityType, entityId, budgetId?, envelopeId? }
 * - actionHint: (optional) short hint like "Review overdue task"
 * 
 * OLD PAYLOADS (still supported):
 * - taskId: string (legacy task notifications)
 * - url: string (legacy custom URL)
 */

/**
 * Resolve notification data to navigation target
 * @param {Object} notification - Notification data object
 * @returns {{ path: string, state?: Object, focusId?: string }}
 */
export function resolveNotificationTarget(notification) {
  // Guard: if no notification data, default to home
  if (!notification) {
    return { path: '/' };
  }

  // Priority 1: Explicit route in notification (new format)
  if (notification.route) {
    return { 
      path: notification.route,
      focusId: notification.entity?.entityId || null
    };
  }

  // Priority 2: Legacy URL field
  if (notification.url) {
    return { path: notification.url };
  }

  // Priority 3: Infer from type + entity
  const type = notification.type || notification.entityType || inferType(notification);
  const entityId = notification.entityId || notification.entity?.entityId;

  switch (type) {
    case 'task':
    case 'task-reminder':
    case 'task-due':
      // Task notifications: navigate to tasks with focus param
      const taskId = notification.taskId || entityId;
      if (taskId) {
        return { 
          path: `/tasks?focus=${encodeURIComponent(taskId)}`,
          focusId: taskId
        };
      }
      return { path: '/tasks' };

    case 'budget':
    case 'envelope':
    case 'budget-warning':
    case 'overspend':
      // Budget notifications: navigate to budget with optional envelope focus
      const envelopeId = notification.envelopeId || notification.entity?.envelopeId || entityId;
      if (envelopeId) {
        return { 
          path: `/budget?focusEnvelope=${encodeURIComponent(envelopeId)}`,
          focusId: envelopeId
        };
      }
      return { path: '/budget' };

    case 'transaction':
      // Transaction notifications
      const txnId = notification.transactionId || entityId;
      if (txnId) {
        return { 
          path: `/transactions?focus=${encodeURIComponent(txnId)}`,
          focusId: txnId
        };
      }
      return { path: '/transactions' };

    case 'shopping':
    case 'shopping-list':
      // Shopping list notifications
      const listId = notification.listId || entityId;
      if (listId) {
        return { 
          path: `/shopping-lists?focus=${encodeURIComponent(listId)}`,
          focusId: listId
        };
      }
      return { path: '/shopping-lists' };

    case 'goal':
    case 'savings-goal':
      // Savings goal notifications
      const goalId = notification.goalId || entityId;
      if (goalId) {
        return { 
          path: `/savings?focus=${encodeURIComponent(goalId)}`,
          focusId: goalId
        };
      }
      return { path: '/savings' };

    case 'system':
    default:
      // System notifications or unknown types: go to home
      return { path: '/' };
  }
}

/**
 * Infer notification type from legacy fields
 * @private
 */
function inferType(notification) {
  if (notification.taskId) return 'task';
  if (notification.envelopeId) return 'budget';
  if (notification.transactionId) return 'transaction';
  if (notification.listId) return 'shopping';
  if (notification.goalId) return 'goal';
  return 'system';
}

/**
 * Parse focus param from URL search params
 * @param {URLSearchParams|string} searchParams 
 * @returns {{ focusId: string|null, focusType: string|null }}
 */
export function parseFocusParams(searchParams) {
  const params = typeof searchParams === 'string' 
    ? new URLSearchParams(searchParams)
    : searchParams;
  
  return {
    focusId: params.get('focus') || params.get('highlight') || params.get('focusEnvelope') || null,
    focusType: params.get('focusEnvelope') ? 'envelope' : 'item'
  };
}

export default resolveNotificationTarget;
